import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../seo';
import { seoConfigs } from '../../utils/seo-configs';
import { useWordPressSEO, useWordPressPosts } from '../../hooks/useWordPress';

function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const { seoData } = useWordPressSEO('blog');
  const { data: blogPosts, loading: postsLoading, error: postsError } = useWordPressPosts('', 20);
  
  // Use WordPress SEO if available, fallback to static config
  const seoProps = seoData ? {
    ...seoConfigs.blog,
    ...seoData
  } : seoConfigs.blog;

  // Extract categories from WordPress posts
  const categories = ['All'];
  if (blogPosts && Array.isArray(blogPosts)) {
    const postCategories = blogPosts.reduce((cats, post) => {
      if (post.categories && Array.isArray(post.categories)) {
        post.categories.forEach(catId => {
          // You might need to map category IDs to names
          // For now, we'll use a simplified approach
          if (!cats.includes('Mental Health')) cats.push('Mental Health');
          if (!cats.includes('Therapy Tips')) cats.push('Therapy Tips');
          if (!cats.includes('Self Care')) cats.push('Self Care');
          if (!cats.includes('Professional Growth')) cats.push('Professional Growth');
          if (!cats.includes('Wellness')) cats.push('Wellness');
        });
      }
      return cats;
    }, []);
    categories.push(...postCategories);
  }

  // Transform WordPress posts to our format
  const transformedPosts = blogPosts && Array.isArray(blogPosts) ? blogPosts.map((post, index) => ({
    id: post.id,
    title: post.title?.rendered || 'Untitled Post',
    excerpt: post.excerpt?.rendered ? (() => {
      const text = post.excerpt.rendered.replace(/<[^>]*>/g, '');
      const el = document.createElement('textarea');
      el.innerHTML = text;
      return el.value.replace(/\s*\[…\]\s*$/, '…').trim();
    })() : 'No excerpt available.',
    author: post._embedded?.author?.[0]?.name || 'TherapyList Team',
    date: new Date(post.date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }),
    readTime: "5 min read", // You could calculate this from content
    category: "Mental Health", // You'd map this from WordPress categories
    image: post._embedded?.['wp:featuredmedia']?.[0]?.source_url || 
           `https://images.unsplash.com/photo-154402799${3 + (index % 6)}-37dbfe43562a?w=600&h=400&fit=crop`,
    featured: index < 2, // Make first 2 posts featured
    slug: post.slug
  })) : [];

  const featuredPosts = transformedPosts.filter(post => post.featured);
  const regularPosts = transformedPosts.filter(post => !post.featured);

  const filteredPosts = selectedCategory === 'All' 
    ? regularPosts 
    : regularPosts.filter(post => post.category === selectedCategory);

  if (postsLoading) {
    return (
      <>
        <SEOHead {...seoProps} />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70] mx-auto mb-4"></div>
            <p className="text-[#254e70]" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>Loading blog posts...</p>
          </div>
        </div>
      </>
    );
  }

  if (postsError) {
    return (
      <>
        <SEOHead {...seoProps} />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center">
            <p className="text-[#254e70] mb-4" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>Unable to load blog posts at this time.</p>
            <button 
              onClick={() => window.location.reload()} 
              className="bg-[#254e70] text-white px-6 py-2 rounded-xl font-semibold hover:scale-105 transition-transform"
              style={{ fontFamily: 'Nunito Sans, sans-serif' }}
            >
              Try Again
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead {...seoProps} />
      
      <div className="min-h-screen bg-[#e4fde1]" style={{ marginTop: '-200px', paddingTop: '200px' }}>
        {/* Header Section */}
        <section className="py-12 lg:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center space-y-4">
              <h1 className="text-4xl lg:text-5xl font-medium text-[#254e70] leading-tight" style={{ fontFamily: 'Kanit, sans-serif' }}>
                TherapyList Blog
              </h1>
              <p className="text-lg text-[#254e70]/80 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                Insights, resources, and expert guidance for mental health professionals and those seeking wellness.
              </p>
            </div>
          </div>
        </section>

        {/* Articles Section with White Background */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 py-16">
          {/* Featured Posts */}
          {featuredPosts.length > 0 && (
            <div className="mb-16">
              <h2 className="text-3xl font-medium text-[#254e70] mb-8 text-center" style={{ fontFamily: 'Kanit, sans-serif' }}>Featured Articles</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {featuredPosts.map((post) => (
                  <article key={post.id} className="group bg-white rounded-3xl overflow-hidden border border-[#9dacff]/20 hover:shadow-2xl transition-all duration-300">
                    <Link to={`/blog/${post.slug}`} className="relative overflow-hidden block">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4 bg-[#254e70] text-white px-4 py-2 rounded-full text-sm font-semibold" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        Featured
                      </div>
                    </Link>
                    <div className="p-8">
                      <div className="flex items-center gap-4 mb-4">
                        <span className="bg-[#9dacff]/10 text-[#254e70] px-3 py-1 rounded-full text-sm font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                          {post.category}
                        </span>
                        <span className="text-[#254e70]/60 text-sm" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.readTime}</span>
                      </div>
                      <Link to={`/blog/${post.slug}`}>
                        <h3 className="text-2xl font-medium text-[#254e70] mb-3 group-hover:text-[#1d3e5a] transition-colors" style={{ fontFamily: 'Kanit, sans-serif' }}>
                          {post.title}
                        </h3>
                      </Link>
                      <p className="text-[#254e70]/70 mb-6 leading-relaxed" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-[#e4fde1] rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-[#254e70]" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div>
                            <p className="font-semibold text-[#254e70] text-sm" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.author}</p>
                            <p className="text-[#254e70]/60 text-xs" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.date}</p>
                          </div>
                        </div>
                        <Link 
                          to={`/blog/${post.slug}`}
                          className="bg-[#254e70] text-white px-6 py-2 rounded-xl font-semibold hover:scale-105 transition-transform inline-block" 
                          style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                        >
                          Read More
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}

          {/* Category Filter */}
          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-2xl font-semibold transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#254e70] text-white shadow-lg'
                      : 'bg-white/80 text-[#254e70] border border-[#9dacff]/20 hover:bg-[#9dacff]/10 hover:border-[#9dacff]/30'
                  }`}
                  style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Regular Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {filteredPosts.map((post) => (
              <article key={post.id} className="group bg-white rounded-2xl overflow-hidden border border-[#9dacff]/20 hover:shadow-xl hover:border-[#9dacff]/30 transition-all duration-300">
                <Link to={`/blog/${post.slug}`} className="relative overflow-hidden block">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </Link>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="bg-[#9dacff]/10 text-[#254e70] px-3 py-1 rounded-full text-xs font-medium" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                      {post.category}
                    </span>
                    <span className="text-[#254e70]/60 text-xs" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.readTime}</span>
                  </div>
                  <Link to={`/blog/${post.slug}`}>
                    <h3 className="text-lg font-medium text-[#254e70] mb-3 group-hover:text-[#1d3e5a] transition-colors line-clamp-2" style={{ fontFamily: 'Kanit, sans-serif' }}>
                      {post.title}
                    </h3>
                  </Link>
                  <p className="text-[#254e70]/70 text-sm mb-4 line-clamp-3" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-[#e4fde1] rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-[#254e70]" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-[#254e70] text-xs" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.author}</p>
                        <p className="text-[#254e70]/60 text-xs" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>{post.date}</p>
                      </div>
                    </div>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="text-[#254e70] hover:text-[#1d3e5a] font-semibold text-sm hover:underline transition-colors" 
                      style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                    >
                      Read More →
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* No Posts Message */}
          {filteredPosts.length === 0 && !postsLoading && (
            <div className="text-center mb-16">
              <p className="text-[#254e70]/70 text-lg" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
                No articles found for "{selectedCategory}" category.
              </p>
            </div>
          )}

          </div>
        </div>
      </div>
    </>
  );
}

export default BlogPage;